import { AnimatedSection } from '@denqfabrik/landing-ui';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../i18n';

/* ── Gradient flow: each card's RIGHT edge matches the next card's LEFT edge ── */

const CARD_GRADIENTS = [
  {
    inactive: 'linear-gradient(104deg, #3d6fc0 0%, #4f92df 100%)',
    active:
      'radial-gradient(circle at 30% 55%, rgba(79, 146, 223, 0.22), transparent 50%), linear-gradient(104deg, #3868b8 0%, #4b8cd8 50%, #4f92df 100%)',
  },
  {
    inactive: 'linear-gradient(104deg, #4f92df 0%, #54b5e4 100%)',
    active:
      'radial-gradient(circle at 35% 50%, rgba(84, 181, 228, 0.18), transparent 50%), linear-gradient(104deg, #4a8cd6 0%, #50a8e0 50%, #54b5e4 100%)',
  },
  {
    inactive: 'linear-gradient(104deg, #54b5e4 0%, #5ccfc8 100%)',
    active:
      'radial-gradient(circle at 40% 50%, rgba(92, 207, 200, 0.18), transparent 50%), linear-gradient(104deg, #50b0e0 0%, #58c4d4 50%, #5ccfc8 100%)',
  },
  {
    inactive: 'linear-gradient(104deg, #5ccfc8 0%, #68dbb8 100%)',
    active:
      'radial-gradient(circle at 55% 50%, rgba(104, 219, 184, 0.20), transparent 50%), linear-gradient(104deg, #58c8c2 0%, #62d4be 50%, #68dbb8 100%)',
  },
];

/* ── Component ────────────────────────────────────────────────────────────── */

export default function ProjectsSection() {
  const { t, lang } = useTranslation();
  const withLang = (to: string) => {
    const prefix = lang === 'en' ? '/en' : '/de';
    return to === '/' ? prefix : `${prefix}${to}`;
  };
  const cs = t.useCases.caseStudies;
  const cards = cs.cards;

  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const total = cards.length;

  useEffect(() => {
    if (total < 2 || isHovering) return;
    const id = window.setInterval(() => {
      setActiveIndex((i) => (i + 1) % total);
    }, 7200);
    return () => window.clearInterval(id);
  }, [total, isHovering]);

  return (
    <section id="projects" className="py-20 md:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <div className="mb-12">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-sky-500">
              {cs.label}
            </p>
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
              {cs.title} <span className="text-slate-400">{cs.titleFaded}</span>
            </h2>
            <p className="mt-4 max-w-3xl text-slate-600">{cs.description}</p>
          </div>
        </AnimatedSection>

        {/* Desktop: expanding accordion */}
        <AnimatedSection delay={120}>
          <div
            className="hidden lg:flex relative h-[500px] gap-3 rounded-[20px] bg-[#e7ebf2] p-0 shadow-[0_24px_90px_rgba(20,39,74,0.12)]"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            {cards.map((card, index) => {
              const isActive = index === activeIndex;
              const grad = CARD_GRADIENTS[index % CARD_GRADIENTS.length];

              return (
                <button
                  key={card.slug}
                  type="button"
                  onMouseEnter={() => setActiveIndex(index)}
                  onFocus={() => setActiveIndex(index)}
                  className={`relative h-full basis-0 overflow-hidden rounded-[20px] text-left transition-[flex-grow] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                    isActive ? 'grow-[7]' : 'grow-[1]'
                  }`}
                  style={{ backgroundImage: isActive ? grad.active : grad.inactive }}
                  aria-label={`Case Study: ${card.title}`}
                  aria-current={isActive}
                >
                  {/* Expanded content */}
                  <div
                    aria-hidden={!isActive}
                    className={`pointer-events-none absolute inset-0 grid h-full grid-cols-[1.1fr_0.9fr] items-center gap-8 p-10 text-white transition-opacity ${
                      isActive
                        ? 'opacity-100 delay-[380ms] duration-100'
                        : 'opacity-0 delay-0 duration-0'
                    }`}
                  >
                    <div>
                      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-white/60">
                        {card.kicker}
                      </p>
                      <h3 className="mb-5 text-4xl font-semibold leading-[1.1] tracking-tight">
                        {card.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-white/85">{card.description}</p>
                    </div>

                    <div>
                      {/* Stat highlight */}
                      <div className="mb-6 rounded-2xl border border-white/20 bg-white/10 p-5 backdrop-blur-sm">
                        <p className="text-4xl font-bold tracking-tight">{card.stat}</p>
                      </div>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-2">
                        {card.tags.map((tag) => (
                          <span
                            key={tag}
                            className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium text-white/80"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Collapsed label */}
                  <div
                    className={`pointer-events-none absolute inset-0 flex flex-col items-center justify-between py-7 transition-opacity ${
                      isActive
                        ? 'opacity-0 delay-0 duration-0'
                        : 'opacity-100 delay-[280ms] duration-200'
                    }`}
                  >
                    <p className="text-[0.6rem] font-bold uppercase tracking-[0.18em] text-white/60">
                      {card.kicker}
                    </p>
                    <p
                      className="whitespace-nowrap text-[0.95rem] font-semibold tracking-tight text-white"
                      style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
                    >
                      {card.title}
                    </p>
                    <span className="h-1.5 w-1.5 rounded-full bg-white/40" />
                  </div>

                  {/* Dots */}
                  <div
                    className={`pointer-events-none absolute bottom-6 left-8 z-20 flex gap-2 transition-opacity ${
                      isActive
                        ? 'opacity-100 delay-[380ms] duration-100'
                        : 'opacity-0 delay-0 duration-0'
                    }`}
                  >
                    {cards.map((_, di) => (
                      <span
                        key={di}
                        className={`h-1.5 rounded-full transition-all duration-150 ${
                          di === activeIndex ? 'w-8 bg-white' : 'w-4 bg-white/40'
                        }`}
                      />
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
        </AnimatedSection>

        {/* Mobile: stacked cards */}
        <div className="space-y-4 lg:hidden">
          {cards.map((card, i) => (
            <AnimatedSection key={card.slug} delay={i * 80}>
              <article className="flex h-full flex-col overflow-hidden rounded-[18px] border border-[#dce2eb] bg-[#eef1f6] p-6 sm:p-8">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-sky-500">
                    {card.kicker}
                  </p>
                  <span className="shrink-0 rounded-full bg-gradient-to-r from-sky-400/15 to-teal-400/15 px-3 py-1 text-xs font-bold text-teal-600">
                    {card.stat}
                  </span>
                </div>
                <h3 className="mt-3 text-[1.2rem] font-semibold leading-[1.25] tracking-tight text-slate-900 sm:text-[1.35rem]">
                  {card.title}
                </h3>
                <p className="mt-3 text-[0.9rem] leading-relaxed text-slate-600">
                  {card.description}
                </p>
                <div className="mt-auto flex flex-wrap gap-2 pt-6">
                  {card.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full border border-[#dce2eb] bg-white/60 px-3 py-1 text-[0.75rem] font-medium text-slate-500"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </article>
            </AnimatedSection>
          ))}
        </div>

        <AnimatedSection delay={350}>
          <div className="mt-10 text-center">
            <Link
              to={withLang('/use-cases')}
              className="inline-flex items-center gap-2 rounded-full border border-[#dce2eb] bg-white/60 px-6 py-3 text-sm font-semibold text-slate-700 transition-all hover:border-slate-400 hover:bg-white"
            >
              {cs.viewAllButton}
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path d="M5 12H19M12 5L19 12L12 19" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}
